/**
 * Polls one export job to a terminal state, then downloads the file.
 *
 * Renders nothing. It lives under `ExportJobsProvider` rather than under the
 * view that started the job, because the user is free to navigate away while
 * the backend materialises the file.
 */
import { type FC, useCallback, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';

import { tpoGenUrls } from 'config/urls';
import { sendNotification } from 'utils/helperMethods';
import { trackTpoGenEvent } from '../../utils/trackTpoGenEvent';
import { tpoGenKeys } from '../../utils/tpoGenQueryKeys';
import useDownloadReportExportMutation from '../hooks/mutations/useDownloadReportExportMutation';
import { readAccessToken } from '../utils/readAccessToken';
import { buildExportEventProps, exportEventName, REPORT_KIND_PAGE_NAMES } from '../utils/exportAnalytics';
import { EXPORT_NOTIFICATIONS, POLL_CEILING_MS, POLL_TIERS } from '../config/exportConstants';
import type { ExportJobRecord, ReportExportStatusResponse } from '../types';

const STATUS_RETRY_COUNT = 2;

const fetchExportStatus = async (
    exportId: string,
    signal: AbortSignal,
): Promise<ReportExportStatusResponse> => {
    const response = await axios(tpoGenUrls.getReportExportStatus(exportId), {
        method: 'GET',
        headers: { Authorization: `Bearer ${readAccessToken()}` },
        signal,
    });
    return response.data;
};

/** First tier the elapsed time has not yet passed; past the ceiling, nothing. */
const pollIntervalFor = (elapsedMs: number): number | false => {
    for (const tier of POLL_TIERS) {
        if (elapsedMs < tier.untilMs) return tier.intervalMs;
    }
    return false;
};

interface ExportJobPollerProps {
    job: ExportJobRecord;
    onSettled: (exportId: string) => void;
}

const ExportJobPoller: FC<ExportJobPollerProps> = ({ job, onSettled }) => {
    const { exportId, reportKind, scope, planId, planCount, createdAt, filename } = job;

    // A terminal status, a failed download and the ceiling timer can all reach
    // the finish line; only the first is allowed to report and remove the job.
    const settledRef = useRef(false);

    const trackOutcome = useCallback(
        (completed: boolean, failureReason?: string) => {
            trackTpoGenEvent(
                exportEventName(reportKind, completed ? 'completed' : 'failed'),
                REPORT_KIND_PAGE_NAMES[reportKind],
                buildExportEventProps(
                    { reportKind, scope, planId, planCount },
                    { durationMs: Date.now() - createdAt, failureReason },
                ),
            );
        },
        [reportKind, scope, createdAt, planCount, planId],
    );

    const fail = useCallback(
        (message: string, fallback: string) => {
            if (settledRef.current) return;
            settledRef.current = true;
            sendNotification('error', message || fallback);
            trackOutcome(false, message || fallback);
            onSettled(exportId);
        },
        [exportId, onSettled, trackOutcome],
    );

    const { downloadExport } = useDownloadReportExportMutation({
        onSuccess: () => {
            sendNotification('success', EXPORT_NOTIFICATIONS.completed);
            trackOutcome(true);
            onSettled(exportId);
        },
        onError: (message) => {
            sendNotification('error', message || EXPORT_NOTIFICATIONS.downloadFailed);
            trackOutcome(false, message || EXPORT_NOTIFICATIONS.downloadFailed);
            onSettled(exportId);
        },
    });

    const { data } = useQuery({
        queryKey: tpoGenKeys.reportExportStatus(exportId),
        queryFn: ({ signal }) => fetchExportStatus(exportId, signal),
        refetchInterval: (query) => {
            const status = query.state.data?.status;
            if (status === 'success' || status === 'error') return false;
            const elapsed = Date.now() - createdAt;
            return elapsed >= POLL_CEILING_MS ? false : pollIntervalFor(elapsed);
        },
        refetchIntervalInBackground: true,
        retry: STATUS_RETRY_COUNT,
        // Overrides the app-wide 1hr staleTime/gcTime: a cached status would
        // report a job that has since finished.
        staleTime: 0,
        gcTime: 0,
    });

    useEffect(() => {
        if (!data || settledRef.current) return;

        if (data.status === 'success') {
            // Not settled here: the job is only done once the user has the file,
            // so the download's own callbacks report the outcome.
            settledRef.current = true;
            // The status resource wins when it names the file, since a rename
            // between create and completion would show up there first.
            downloadExport({ exportId, filename: data.filename ?? filename });
            return;
        }
        if (data.status === 'error') {
            fail(data.error_message ?? '', EXPORT_NOTIFICATIONS.failed);
        }
    }, [data, exportId, filename, downloadExport, fail]);

    // Hard ceiling. A job that finishes after this is simply never downloaded,
    // which is the agreed behaviour rather than an unbounded poll.
    useEffect(() => {
        const remaining = Math.max(createdAt + POLL_CEILING_MS - Date.now(), 0);
        const timer = setTimeout(() => fail('', EXPORT_NOTIFICATIONS.timedOut), remaining);
        return () => clearTimeout(timer);
    }, [createdAt, fail]);

    return null;
};

ExportJobPoller.displayName = 'ExportJobPoller';
export default ExportJobPoller;
